/**
 * @file Read-only contract card body: contract facts, monthly charge templates
 * with each template's payment-route label, and — when `props.switcher` is set —
 * the contract-level `PaymentRouteRadio` driving the host page's route handler,
 * followed by the editable `referenceForTenant` input beneath it.
 *
 * Flow: rendered by ContractCard (props spread-forwarded from the host page) →
 * reads `props.contract` / `props.paymentRouteId` / `props.ownerName` → route
 * changes call the host's route-handler prop with the chosen `PaymentRouteId`;
 * the reference input's blur calls `props.handleReferenceForTenantSave`, the
 * only route reaching that column from a non-concept contract (the general
 * contract PUT writes it too, but answers 403 in that state).
 * Exceptions: none.
 */
import * as React from "react";
import Component from "../../AbstractComponent";
import { ContractInterface } from "../../../Entity/Entities/Contract/ContractInterface";
import { ChargeTemplateInterface } from "../../../Entity/Entities/Charge/ChargeTemplateInterface";
import PeriodContainer from "../../../Entity/Entities/Charge/Period/PeriodContainer";
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import TextField from "../Material/TextField";
import { Person } from "../../../Entity/Entities/Person/Person";
import Constants from "../../../Action/Constants";
import PaymentRouteRadio from "../Charge/PaymentRouteRadio";
import { PAYMENT_ROUTE } from "../../../Entity/Entities/Charge/PaymentRoute";

export default class ContractDetailsReadonly extends Component {
  public state = {

  };

  constructor(public props) {
    super(props);
  }

  public getReadonlyReason = (contract: ContractInterface) => {
    if (contract?.removed) {
      return this.trans("contract-readonly/removed");
    } else if (!contract?.isConcept) {
      return this.trans("contract-readonly/signed");
    }
  }

  public render() {
    const contract: ContractInterface = this.props.contract;
    const monthlyChargeTemplates: ChargeTemplateInterface[] = [...(contract?.chargeTemplates || [])]
      .filter((chargeTemplate: ChargeTemplateInterface) => chargeTemplate?.repeatPeriod?.id === PeriodContainer.pickIdFromLabel("monthly"));
    const monthlyChargeTemplatesAmount: number = monthlyChargeTemplates
      .map((chargeTemplate: ChargeTemplateInterface) => chargeTemplate.amount)
      .reduce((a, b) => a + b, 0);
    const occupants = (contract?.occupants || []).map(occupant => {
      return `${occupant.person.displayName}`
    })
    const previousContract = this.props.app?.contracts.find(contract2 => contract2.id === contract.previousContractId);
    const reminders = this.props.reminders?.length ? this.props.reminders : this.props.events?.reminders.filter(reminder => reminder.contractId === this.props.contract.id && reminder.removed === false)
    
    return (
      <div className="contract-details-readonly">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "baseline" }}>
            <div style={{ marginRight: "7px", fontSize: "17px", fontWeight: "bold" }}>
              {this.trans("label-contract")}
            </div>
            <div className="title" style={{ color: contract?.getStatusColor() }}>
              {this.trans(contract?.contractStatus)}
            </div>
          </div>
          <div className="date">
            {this.formatter.formatDate(contract?.firstDate)}
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div className="counterparty">
            {`${contract?.tenantDisplayName}`}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div className="currency">
              {
                !!monthlyChargeTemplatesAmount ?
                  contract?.chargeTemplates?.[0].currency?.getIso() :
                  this.props.app?.settings?.user?.currency?.getIso()
              }
            </div>
            <div className="amount">
              {
                !!monthlyChargeTemplatesAmount ?
                  this.formatter.formatNumber(monthlyChargeTemplatesAmount) :
                  this.formatter.formatNumber(monthlyChargeTemplatesAmount)
              }
            </div>
          </div>
        </div>
        <table>
          <tr>
            <td className="align-left first-column">
              {`${this.trans("unit")}: `}
            </td>
            <td className="align-left">
              <div className="margin-wrapper">
                <div
                  className="link-to-entity"
                  onClick={() => this.redirect(`/app/unit/update/${contract?.unitId}/`)}
                >
                  {`${contract?.unit?.displayName ? contract?.unit?.displayName : contract?.unit?.shortDisplayName }`}
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td className="align-left first-column">
              {`${this.trans("tenant")}:`}
            </td>
            <td className="align-left">
              <div className="margin-wrapper">
                <div
                  className="link-to-entity"
                  onClick={() => this.redirect(`/app/${contract?.tenant instanceof Person ? 'person' : 'company'}/update/${contract?.tenant?.id}`)}
                >
                  {`${contract?.tenant?.displayName}`}
                </div>
              </div>
            </td>
          </tr>
          {
            occupants.length > 0 && (
              <tr>
                <td className="align-left first-column">
                  {`${this.trans("label-occupants")}:`}
                </td>
                <td className="align-left">
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {
                      occupants.map((occupant: string) => <div>{occupant}</div>)
                    }
                  </div>
                </td>
              </tr>
            )
          }
          {
            contract?.firstDate && (
              <tr>
                <td className="align-left first-column">
                  {`${this.trans("first-date")}:`}
                </td>
                <td className="align-left">
                  {this.formatter.formatDate(contract?.firstDate)}
                </td>
              </tr>
            )
          }
          {
            contract?.lastDate && (
              <tr>
                <td className="align-left first-column">
                  {`${this.trans("expiration-date")} 1:`}
                </td>
                <td className="align-left">
                  {this.formatter.formatDate(contract?.lastDate)}
                </td>
              </tr>
            )
          }
          {
            contract?.finalRentalPeriodDate && (
              <tr>
                <td className="align-left first-column">
                  {`${this.trans("expiration-date")} 2:`}
                </td>
                <td className="align-left">
                  {this.formatter.formatDate(contract?.finalRentalPeriodDate)}
                </td>
              </tr>
            )
          }
          {
            contract?.endDate && (
              <tr>
                <td className="align-left first-column">
                  {`${this.trans("end-date")}:`}
                </td>
                <td className="align-left">
                  {this.formatter.formatDate(contract?.endDate)}
                </td>
              </tr>
            )
          }

          
          {reminders?.length > 0 && (
            <tr>
              <td className="align-left first-column">
                {`${this.trans("menuitem-notifications")}:`}
              </td>

              <div style={{ display: "flex", flexDirection: "column" }}>
                <>
                
                  {reminders.filter(reminder => reminder.contractId === this.props.contract.id && reminder.removed === false).map((event, index) => {
                    return (
                      <div style={{ display: "flex", marginBottom: index + 1 !== monthlyChargeTemplates.length ? "3px" : 0 }}>
                        <div style={{ marginRight: "5px" }}>
                          {event.remindDate ? this.formatter.formatDate(event.remindDate) : null}
                          {event.content?.name ? ` ${event.content?.name}` : ''}
                        </div>

                        {
                          contract?.isReadonly && this.props.mainCardPresentation &&
                          <>
                            <Link
                              onClick={() => {
                                if (typeof this.props.openExpirationContractDialog === "function") {
                                  this.props.openExpirationContractDialog(event);
                                }
                              }}
                              style={{ marginLeft: "5px" }}
                            >
                              {this.trans("button-update")}
                            </Link>

                            <Link
                              onClick={() => {
                                if (event.id) {
                                  this.props.dispatch({
                                    type: Constants.GLOBAL_EVENT_DELETE_START,
                                    payload: {
                                      event: event,
                                    },
                                    onSuccess: () => {
                                      this.props.fetchEntityEvents(this.props.contract?.id)
                                      // this.props.dispatch({
                                      //   type: Constants.GLOBAL_CONVERSATION_READ_UPDATE_START,
                                      //   payload: {
                                      //     conversationId: this.props.app.conversations?.find(c => c.eventId === event.id)?.id
                                      //   }
                                      // });
                                    },
                                    onError: () => {
                                      this.setState({
                                        isSaving: false
                                      });
                                    }
                                  });
                                }
                              }}
                              style={{ marginLeft: "5px" }}
                            >
                              {this.trans("menuitem-delete")}
                            </Link>
                          </>

                        }
                      </div>
                    );
                  })
                  }
                </>

              </div>

            </tr>
          )
          }
          {
            monthlyChargeTemplates.length > 0 && (
              <tr>
                <td className="align-left first-column">
                  {`${this.trans("title-contract-prices")}:`}
                </td>
                <td className="align-left">
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {
                      monthlyChargeTemplates.map((chargeTemplate: ChargeTemplateInterface, index) => {
                        return (
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: index + 1 !== monthlyChargeTemplates.length ? "3px" : 0 }}>
                            <div style={{ marginRight: "5px" }}>
                              {`${this.formatter.checkTranslationType(this.trans(chargeTemplate?.type?.label, {}, "", chargeTemplate?.type?.system), chargeTemplate?.type?.label, chargeTemplate?.type?.system)} ${this.formatter.formatNumber(chargeTemplate?.amount, chargeTemplate?.currency)}`}
                            </div>
                            {
                              chargeTemplate?.paymentRouteId === PAYMENT_ROUTE.DIRECT_UNTRACKED && (
                                <div style={{ padding: "2px", display: "flex", justifyContent: "center", alignItems: "center", margin: index + 1 !== monthlyChargeTemplates.length ? "-3px 0 0 5px" : "-3px 0 0 0" }}>
                                  <div className="list-label creation-type-label">
                                    {this.trans("label-direct")}
                                  </div>
                                </div>
                              )
                            }
                          </div>
                        );
                      })
                    }
                  </div>
                </td>
              </tr>
            )
          }
          {
            contract?.previousContractId && (
              <tr>
                <td className="align-left first-column">
                  {`${this.trans("textfield-previous-contract")}`}
                </td>
                <td className="align-left">
                  <div className="margin-wrapper">
                    <div
                      className="link-to-entity"
                      onClick={() => this.redirect(`/app/contract/update/${previousContract?.id}/`)}
                    >
                      {`${previousContract?.displayName}`}
                    </div>
                  </div>
                </td>
              </tr>
            )
          }
        </table>
        {
          contract?.isReadonly && this.props.mainCardPresentation &&
          <div className="readonly-reason" style={{ flexDirection: "column" }}>
            <div style={{ display: "flex", flexDirection: "row" }}>
              <div
                className="list-label readonly"
                style={{
                  marginRight: "5px",
                  maxWidth: "max-content",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {contract?.isReadonly && this.trans("label-readonly")}
              </div>
              <div style={{ letterSpacing: '0.00938em' }}>{this.getReadonlyReason(contract)}</div>
            </div>
          </div>
        }
        {
          this.props.switcher && (
            <FormControlLabel
              control={
                <Switch
                  checked={contract.isCommercial}
                  onChange={this.props.handleVATSwitcher}
                  color="primary"
                />
              }
              label={this.trans("page/contracts-label/iscommercialcontract", { vatPercentage: this.props?.app?.settings?.agency?.vatPercentage })}
            />
          )
        }
        <div style={{ display: "flex", alignItems: "center" }}>
          {this.props.switcher && (
            <FormControlLabel
              control={
                <Switch
                  checked={contract.autoSendInvoiceToTenant}
                  onChange={this.props.handleAutoSendInvoiceToTenantSwitch}
                  color="primary"
                />
              }
              style={{ marginRight: 0 }}
              label={contract.autoSendInvoiceToTenant ? this.trans("page/contracts-label/automaticallysendrentinvoice") + ': ' : this.trans("page/contracts-label/automaticallysendrentinvoice")}
            />
          )}

          {this.props.switcher && contract.autoSendInvoiceToTenant === true && (
            <div style={{ display: "inline" }}>
              <span>&nbsp;{this.props.contract?.addressForRentEmail ? this.props.contract.addressForRentEmail : this.props.contract?.person.email}</span>
              <Link
                onClick={() => {
                  if (typeof this.props.openChangeEmailDialogOpen === "function") {
                    this.props.openChangeEmailDialogOpen();
                  }
                }}
                style={{ marginLeft: "5px" }}
              >
                {this.trans("change-email")}
              </Link>
            </div>
          )}
        </div>

        {
          this.props.switcher && (
            <PaymentRouteRadio
              value={this.props.paymentRouteId}
              onChange={this.props.switchIsPaidDirectlyToOwner}
              agencyName={this.getAgency()?.name}
              ownerName={this.props.ownerName}
            />
          )
        }
        {
          // Same field the editable body carries, on its own row under the route
          // radios. A non-concept contract is refused by the general PUT, so this
          // one saves itself on blur through the host page's dedicated-route handler.
          this.props.switcher && (
            <div style={{ width: "60%" }}>
              <TextField
                autoComplete="off"
                id="referenceForTenant"
                name="referenceForTenant"
                className="form-element full-size scroll-to-referenceForTenant"
                type="text"
                label={this.trans("label-reference-for-tenant") + this.trans("field/optional")}
                value={this.props.referenceForTenant}
                onChange={this.props.handleReferenceForTenantChange}
                onBlur={this.props.handleReferenceForTenantSave}
                inputProps={{ maxLength: 255 }}
                variant="standard"
                disabled={contract.removed}
                // Fed by the host page from the same `component.contractUpdate.fields`
                // slice the dedicated route's FAILURE writes, so a rejection lights this
                // input rather than only reaching the snackbar.
                error={!!this.props.referenceForTenantError}
                helperText={this.props.referenceForTenantError}
              />
            </div>
          )
        }
        {
          !this.props.mainCardPresentation && (
            <div style={{ marginTop: "10px", display: "flex", justifyContent: this.props.unActionButton ? "space-between" : "flex-start" }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => this.redirect(`/app/contract/update/${contract?.id}/0`)}
              >
                {this.trans("menuitem-open")}
              </Button>
              {this.props.unActionButton}
            </div>
          )
        }
      </div >
    );

  }
}